import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DropdownOption } from 'models/Dropdown';
import TrashIcon from 'icons/Trash.icon';
import RefreshIcon from 'icons/Refresh.icon';
import ClipboardcopyIcon from 'icons/Clipboardcopy.icon';
import SettingsIcon from 'icons/Settings.icon';
import CalendarIcon from 'icons/Calendar.icon';
import useCopyToClipboard from 'hooks/useCopyToClipboard';
import { getResolveUrl, getShareableLinkRoute } from 'utils';
import useChangeShareableLinkState, {
  UseShareableLinkActionProps,
} from './useChangeShareableLinkState';
import useManageShareableLink from './useManageShareableLink';
import useProlongSecureLink from 'router/subrouters/DataRoom/pages/Dashboard/componenets/SuggestedActions/hooks/useProlongSecureLink';
import { DataRoomShareableLink } from 'models/ShareableLink';

const useGetShareableLinkActionOptions = (
  props: UseShareableLinkActionProps,
) => {
  const { link, updateShareableLink } = props;

  const { _id, linkExpiresDate } = link;

  const { t } = useTranslation();
  const copyToClipboard = useCopyToClipboard();

  const { changeState, isActiveLink, isChangeStateDisabled } =
    useChangeShareableLinkState(props);

  const onSuccessProlong = useCallback(
    (shareableLink: Partial<DataRoomShareableLink>) => {
      updateShareableLink?.({ ...link, ...shareableLink });
    },
    [link, updateShareableLink],
  );

  const { openProlongModal, prolongSecureLinkModal } =
    useProlongSecureLink(onSuccessProlong);
  const { manageShareableLink, isManageShareableLinkDisabled } =
    useManageShareableLink(link);

  const options = useMemo<DropdownOption[]>(
    () => [
      {
        label: t('manage'),
        icon: <SettingsIcon />,
        disabled: isManageShareableLinkDisabled,
        onClick: manageShareableLink,
      },
      ...(isActiveLink
        ? [
            {
              label: t('prolong'),
              icon: <CalendarIcon />,
              disabled: isChangeStateDisabled,
              onClick: () => openProlongModal(_id, linkExpiresDate),
            },
          ]
        : []),
      {
        label: t('copyLink'),
        icon: <ClipboardcopyIcon />,
        onClick: (e) =>
          copyToClipboard(e, getResolveUrl(getShareableLinkRoute(_id))),
      },
      {
        label: t(isActiveLink ? 'revoke' : 'renew'),
        icon: isActiveLink ? <TrashIcon /> : <RefreshIcon />,
        alert: isActiveLink,
        disabled: isChangeStateDisabled,
        onClick: changeState,
      },
    ],
    [
      t,
      _id,
      linkExpiresDate,
      isActiveLink,
      isChangeStateDisabled,
      isManageShareableLinkDisabled,
      changeState,
      openProlongModal,
      copyToClipboard,
      manageShareableLink,
    ],
  );

  return { options, prolongShareableLinkModal: prolongSecureLinkModal };
};

export default useGetShareableLinkActionOptions;
